/**
 * Allowlisted BACK targets via `location.state.from` (Android-style stack).
 * Shared by Live and company detail. Reject unknown strings so state cannot become an open redirect.
 */

export type LiveBackFrom =
  | "/"
  | "/dashboard"
  | "/companies"
  | "/projects"
  | `/projects?company=${string}`
  | `/companies/${string}`
  | `/companies/${string}?tab=projects`;

export type LiveBackTarget = {
  to: string;
  labelKey: "dashboardExtra.backToDashboard" | "projectsList.backToCompanies" | "projectLive.back";
};

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const COMPANY_QUERY = new RegExp(`^/projects\\?company=${UUID}$`, "i");
/** `/companies/<uuid>` or `?tab=projects` only — bad UUIDs and other tabs are rejected. */
const COMPANY_DETAIL = new RegExp(`^/companies/${UUID}(?:\\?tab=projects)?$`, "i");

/** Navigation state payload when opening Live from a known parent. */
export function liveFromState(from: LiveBackFrom): { from: LiveBackFrom } {
  return { from };
}

/**
 * Resolve BackLink target from optional `location.state.from`.
 * Unrecognized / missing → `defaultTo` with `defaultLabelKey` (Live: `projectLive.back`;
 * company detail: `projectsList.backToCompanies`).
 */
export function resolveLiveBack(
  from: unknown,
  defaultTo: string,
  defaultLabelKey: LiveBackTarget["labelKey"] = "projectLive.back",
): LiveBackTarget {
  if (typeof from !== "string") {
    return { to: defaultTo, labelKey: defaultLabelKey };
  }
  if (from === "/" || from === "/dashboard") {
    return { to: "/", labelKey: "dashboardExtra.backToDashboard" };
  }
  if (from === "/companies") {
    return { to: "/projects", labelKey: "projectsList.backToCompanies" };
  }
  if (from === "/projects" || COMPANY_QUERY.test(from) || COMPANY_DETAIL.test(from)) {
    return { to: from, labelKey: "projectLive.back" };
  }
  return { to: defaultTo, labelKey: defaultLabelKey };
}
